import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  input,
  OnInit,
  output,
  signal,
  untracked,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgTemplateOutlet } from '@angular/common';
import { KENDO_DROPDOWNS } from '@progress/kendo-angular-dropdowns';
import { TrimWhitespaceDirective } from '../../../../core/directives/trim.directive';
import { PhoneFormatDirective } from '../../../../core/directives/phone-format.directive';
import { trimFormGroup } from '../../../../core/utils/form.utils';
import { ApiService } from '../../../../core/services/api.service';
import { ToastService } from '../../../../core/services/toast.service';
import { S3ImageUploadService } from '../../../../core/services/s3-image-upload.service';
import { DataService } from '../../../../core/services/data.service';
import { API_ROUTES } from '../../../../core/constants/api-routes';
import { AddFamilyMemberRequest, FamilyMemberDetail, FamilyMemberItem, LovedOnesFamilySpace, UpdateFamilyMemberRequest } from '../../../../core/models/loved-ones.models';
import { MasterListData, RelationshipItem } from '../../../../core/models/story.models';

@Component({
  selector: 'app-add-family-member',
  templateUrl: './add-family-member.html',
  styleUrl: './add-family-member.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, TrimWhitespaceDirective, PhoneFormatDirective, KENDO_DROPDOWNS, NgTemplateOutlet],
})
export class AddFamilyMember implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly api = inject(ApiService);
  private readonly toast = inject(ToastService);
  private readonly s3 = inject(S3ImageUploadService);
  private readonly dataService = inject(DataService);

  /** Family space ID passed from parent (used in edit mode / fallback). */
  readonly familySpaceId = input<string>('');

  /** All family spaces — shown in the dropdown in add mode. */
  readonly familySpaces = input<LovedOnesFamilySpace[]>([]);

  /** Member to edit — null for add mode. */
  readonly editMember = input<FamilyMemberItem | null>(null);
  /** When true renders as an inline expand panel instead of a modal. */
  readonly isInline = input(false);

  /** Emits when member is successfully added/updated. */
  readonly memberAdded = output<void>();
  /** Emits when user cancels / closes. */
  readonly closed = output<void>();

  readonly isLoading = signal(false);
  readonly isPhotoUploading = signal(false);
  readonly photoPreview = signal<string | null>(null);
  readonly isPhotoDragOver = signal(false);
  /** True while fetching the full member detail for edit mode. */
  readonly isFetchingDetail = signal(false);
  private photoS3Key = '';

  readonly relationshipOptions = signal<RelationshipItem[]>([]);

  readonly memberForm = this.fb.group({
    selectedFamilySpaceId: ['' as string, [Validators.required]],
    firstName:    ['', [Validators.required, Validators.minLength(2)]],
    lastName:     ['', [Validators.required, Validators.minLength(2)]],
    relationship: [null as number | null, [Validators.required]],
    email:        ['', [Validators.required, Validators.email]],
    phoneNumber:  [''],
  });

  get f() { return this.memberForm.controls; }

  constructor() {
    effect(() => {
      const member = this.editMember();
      untracked(() => {
        this.memberForm.reset();
        this.photoPreview.set(null);
        this.photoS3Key = '';
        if (member) {
          // In edit mode pre-fill the family space and disable the dropdown
          this.memberForm.patchValue({
            selectedFamilySpaceId: this.familySpaceId(),
          });
          this.memberForm.get('selectedFamilySpaceId')!.disable();
          this.memberForm.patchValue({
            firstName:   member.fullName?.split(' ')[0] ?? '',
            lastName:    member.fullName?.split(' ').slice(1).join(' ') ?? '',
            email:       member.email       ?? '',
            phoneNumber: member.phoneNumber ?? '',
          });
          this.loadMemberDetail(member.familyMemberId, this.familySpaceId());
        }
      });
    });
  }

  ngOnInit(): void {
    this.loadRelationships();
  }

  /** Fetches the full member detail and updates the relationship dropdown for edit mode. */
  private async loadMemberDetail(familyMemberId: string, familySpaceId: string): Promise<void> {
    this.isFetchingDetail.set(true);
    try {
      const resp = await this.api.request<FamilyMemberDetail>(
        'GET',
        `${API_ROUTES.GET_FAMILY_MEMBER_DETAIL_BY_ID}/${familyMemberId}/${familySpaceId}`,
        undefined,
        { showToaster: false, showLoader: false, useToken: true },
      );
      if (resp.status && resp.data) {
        const d = resp.data;
        this.memberForm.patchValue({
          firstName:    d.firstName         ?? '',
          lastName:     d.lastName          ?? '',
          email:        d.email             ?? '',
          phoneNumber:  d.phoneNumber       ?? '',
          relationship: d.relationshipTypeId ?? null,
        });
        if (d.profilePhotoName) {
          this.photoS3Key = d.profilePhotoName;
          if (d.profilePhotoUrl) {
            this.photoPreview.set(d.profilePhotoUrl);
          }
        } else {
          this.photoS3Key = '';
          this.photoPreview.set(null);
        }
      }
    } catch { /* silent — form retains the basic data already patched */ }
    finally {
      this.isFetchingDetail.set(false);
    }
  }

  private async loadRelationships(): Promise<void> {
    try {
      const resp = await this.api.request<MasterListData>(
        'POST',
        API_ROUTES.COMMON_GET_ALL_MASTER_LIST,
        { typeIds: [4] },
        { showToaster: false, showLoader: false, useToken: true },
      );
      if (resp.status && resp.data?.relationshipList?.length) {
        this.relationshipOptions.set(resp.data.relationshipList);
      }
    } catch { /* silent — dropdown stays empty */ }
  }

  async onPhotoSelected(event: Event): Promise<void> {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file) return;
    await this.uploadPhoto(file);
  }

  onPhotoDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    if (!this.isPhotoUploading()) this.isPhotoDragOver.set(true);
  }

  onPhotoDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isPhotoDragOver.set(false);
  }

  async onPhotoDrop(event: DragEvent): Promise<void> {
    event.preventDefault();
    event.stopPropagation();
    this.isPhotoDragOver.set(false);
    if (this.isPhotoUploading()) return;
    const file = event.dataTransfer?.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      this.toast.error('Only image files are allowed.');
      return;
    }
    await this.uploadPhoto(file);
  }

  private async uploadPhoto(file: File): Promise<void> {
    if (file.size === 0) {
      this.toast.error('Empty files (0 KB) are not allowed. Please select a valid file.');
      return;
    }
    this.isPhotoUploading.set(true);
    try {
      const result = await this.s3.upload(file, 'memberPhoto');
      this.photoS3Key = result.fileName;
      this.photoPreview.set(result.url);
    } catch {
      this.toast.error('Failed to upload photo. Please try again.');
    } finally {
      this.isPhotoUploading.set(false);
    }
  }

  removePhoto(): void {
    this.photoS3Key = '';
    this.photoPreview.set(null);
  }

  async onSubmit(): Promise<void> {
    trimFormGroup(this.memberForm);
    if (this.memberForm.invalid) {
      this.memberForm.markAllAsTouched();
      return;
    }

    this.isLoading.set(true);
    const { selectedFamilySpaceId, firstName, lastName, relationship, email, phoneNumber } = this.memberForm.getRawValue();
    const resolvedFamilySpaceId = selectedFamilySpaceId || this.familySpaceId();
    const member = this.editMember();

    let resp;
    if (member) {
      const body: UpdateFamilyMemberRequest = {
        familyMemberId:       member.familyMemberId,
        firstName:            firstName!,
        lastName:             lastName!,
        email:                email!,
        phoneNumber:          phoneNumber!,
        profilePhotoFileName: this.photoS3Key || '',
        relationshipTypeId:   relationship!,
        familySpaceId:        resolvedFamilySpaceId,
      };
      resp = await this.api.request(
        'POST',
        API_ROUTES.UPDATE_FAMILY_MEMBER,
        body,
        { showToaster: true, showLoader: false, useToken: true },
      );
    } else {
      const body: AddFamilyMemberRequest = {
        firstName:            firstName!,
        lastName:             lastName!,
        email:                email!,
        phoneNumber:          phoneNumber!,
        profilePhotoFileName: this.photoS3Key || '',
        relationshipTypeId:   relationship!,
        familySpaceId:        resolvedFamilySpaceId,
      };
      resp = await this.api.request(
        'POST',
        API_ROUTES.ADD_FAMILY_MEMBER,
        body,
        { showToaster: true, showLoader: false, useToken: true },
      );
    }

    if (resp.status) {
      // When editing the logged-in user's own record, sync all changed fields to
      // DataService so the header name, email, and avatar update without a refresh.
      if (this.editMember()?.isLoggedInUser) {
        const current = this.dataService.getProfile();
        if (current) {
          const { firstName, lastName, email } = this.memberForm.value;
          const newPhoto = this.photoPreview();
          this.dataService.setProfile({
            ...current,
            firstName:        firstName  ?? current.firstName,
            lastName:         lastName   ?? current.lastName,
            fullName:         `${firstName ?? ''} ${lastName ?? ''}`.trim() || current.fullName,
            email:            email      ?? current.email,
            profileImagePath: newPhoto   ?? current.profileImagePath,
          });
        }
      }
      this.memberAdded.emit();
    }
    this.isLoading.set(false);
  }
}
